/**
 * The cover — `docs/proposal.md` §6 item 2.
 *
 * A band of the company's colour with faint contour rings over it, the quote
 * reference and date as an eyebrow, the job's title at 52px, and who it is
 * for. It is the one piece of the page that is purely the company's: by the
 * time somebody has scrolled past it they know whose document they are
 * reading.
 *
 * **380px, not §6's 330** — `cp-01-proposal-top.html` draws 380 and the html
 * is the pixel reference (`design-system.md` §0). 236 on a phone, from
 * `cp-08-phone.html`. The disagreement is recorded in the PR body.
 *
 * **Without the band** the cover is the page's own background with a 6px rule
 * of the brand colour across the top and ink text — board 6's "Copperline",
 * which is also where a near-white brand lands (§5.3): a white band on a
 * white page is not a cover, and drawing one anyway is how a brand colour
 * breaks a document.
 */
import { useMemo } from 'react'
import { cn } from '../../lib/cn.js'
import { contourRings } from './contours.js'

export function ProposalCover({
  reference, quoteDate, title, preparedFor, address, band, phone, narrow = false,
}: {
  reference: string
  quoteDate: string
  title: string
  preparedFor: string
  address: string
  band: boolean
  phone: boolean
  /**
   * Below the summary rail: the words sit over the page's 740 column. Decided
   * by the page, not by `max-[1099px]:`, so the builder's iPad frame draws
   * what an iPad does.
   */
  narrow?: boolean
}) {
  const height = phone ? 236 : 380
  const width = phone ? 390 : 1280

  /* Board 1 says "Proposal Q-3102" and nothing else: the date is the intro card's. */
  const eyebrow = `Proposal ${reference}`
  void quoteDate

  return (
    <div
      style={{ height }}
      className={cn('relative shrink-0 overflow-hidden',
        band ? 'bg-brand text-brand-on' : 'border-b border-line-soft bg-surface text-text')}
    >
      {band && <Contours width={width} height={height} />}
      {/* Board 6's band-off cover: a 6px rule of the brand across the top. */}
      {!band && <i aria-hidden="true" className="absolute inset-x-0 top-0 block h-1.5 bg-brand" />}

      <div className={cn('relative',
        phone ? 'max-w-[1280px] px-5 pt-7'
          : narrow ? 'mx-auto max-w-[820px] px-10 pt-[54px]'
            : 'max-w-[1280px] px-[60px] pt-[54px]')}>
        <div className={cn('flex items-center gap-2.5 font-bold uppercase tracking-[0.1em]',
          phone ? 'text-[11px]' : 'text-[12px]',
          band ? 'opacity-[0.86]' : 'text-brand-tx')}>
          <span>{eyebrow}</span>
        </div>
        <h1 className={cn('m-0 font-bold leading-[1.06] tracking-[-0.025em]',
          phone ? 'mt-2.5 max-w-[350px] text-[30px]' : 'mt-3.5 max-w-[800px] text-[52px]')}>
          {title}
        </h1>
        <div className={cn('leading-[1.45]',
          phone ? 'mt-3 text-[14.5px]' : 'mt-5 text-[17px]',
          band ? 'opacity-95' : 'text-text-body')}>
          Prepared for <b className={cn('font-semibold', !band && 'text-text')}>{preparedFor}</b>
          {/* Board 6: on the white cover the address follows on the same line. */}
          {address && (band
            ? (
              <>
                <br />
                <span className="opacity-[0.85]">{address}</span>
              </>
              )
            : ` · ${address}`)}
        </div>
      </div>
    </div>
  )
}

/**
 * The rings, stretched to the band.
 *
 * `preserveAspectRatio="none"` is the mockup's own, which is safe here only
 * because `contourRings` is given this box's dimensions: it draws for the
 * space it is in rather than being squashed into it.
 *
 * `aria-hidden` because it is a texture. A screen reader announcing twelve
 * unnamed paths at the top of a proposal is worse than silence.
 */
function Contours({ width, height }: { width: number; height: number }) {
  const rings = useMemo(() => contourRings(width, height), [width, height])
  return (
    <svg
      aria-hidden
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="absolute inset-0 h-full w-full"
    >
      {rings.map((ring, index) => (
        <path
          key={index}
          d={ring.d}
          fill="none"
          stroke="var(--contour)"
          strokeWidth={1.4}
          opacity={ring.opacity}
        />
      ))}
    </svg>
  )
}
